/**
 * Supabase client configuration
 * Provides authenticated Supabase client for the application
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { env } from '../../config/env';

// Create Supabase client with auth configuration
export const supabase: SupabaseClient = createClient(
  env.supabase.url,
  env.supabase.anonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
    },
  }
);

// Note: Service role keys should NEVER be exposed to the frontend
// Instead, we rely on RLS policies to control access
// The "Authenticated users can create venues" policy allows venue creation

/**
 * Get current session
 */
export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

/**
 * Get current user
 */
export async function getUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data.user;
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) throw error;
  return data;
}

/**
 * Sign out
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Get venue ID from JWT claims
 */
export function getVenueIdFromSession(session: any): string | null {
  if (!session?.user?.user_metadata?.venue_id) {
    return null;
  }
  return session.user.user_metadata.venue_id;
}

/**
 * Update user metadata with venue ID
 */
export async function updateUserMetadata(venueId: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    data: { venue_id: venueId }
  });
  
  if (error) throw error;
}

/**
 * Create a new venue
 * Relies on RLS policy "Authenticated users can create venues" to allow INSERT
 */
export async function createVenue(name: string): Promise<string> {
  console.log('[createVenue] Attempting to create venue:', name);
  
  const { data, error } = await supabase
    .from('venues')
    .insert({ name })
    .select('id')
    .single();
  
  if (error) {
    console.error('[createVenue] Supabase error:', error);
    console.error('[createVenue] Error details:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    throw error;
  }
  
  if (!data) {
    console.error('[createVenue] No data returned from insert');
    throw new Error('Failed to create venue - no data returned');
  }
  
  console.log('[createVenue] Venue created successfully:', data.id);
  return data.id;
}

/**
 * Fetch venues accessible to the current user
 */
export async function fetchUserVenues(): Promise<Array<{ id: string; name: string; created_at: string; updated_at: string }>> {
  const { data, error } = await supabase
    .from('venues')
    .select('id, name, created_at, updated_at')
    .order('name', { ascending: true });
  
  if (error) throw error;
  return data || [];
}
